/**
 * VendorApprovalGate — Inserted between PrivateRoute and the /vendor/*
 * routes. Loads the current vendor profile once, then either renders the
 * outlet (admin masquerade or approval_status === 'approved') or swaps
 * in <PendingApproval />.
 *
 * The /vendor/profile route is always allowed through so a pending or
 * not-yet-submitted vendor can still complete / edit their submission
 * while the admin queue clears.
 */
import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@hooks/useAuth';
import vendorService from '@services/vendorService';
import PendingApproval from '@components/vendor/PendingApproval';
import Loader from '@components/common/Loader';

const PROFILE_PATH = '/vendor/profile';

const VendorApprovalGate = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [vendor, setVendor] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Admins / super_admins can browse the vendor portal for support
    // purposes; the approval gate is for vendor-role users only.
    if (user?.role !== 'vendor') { setLoaded(true); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await vendorService.getMyProfile();
        if (!cancelled) setVendor(r?.data?.vendor || r?.vendor || null);
      } catch {
        // Network or 404 (no profile yet) — fall through to the "setup"
        // branch below, which renders the PendingApproval screen unless
        // the user is already on /vendor/profile.
      }
      if (!cancelled) setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [user?.role]);

  if (!loaded) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 320 }}>
        <Loader size="lg" />
      </div>
    );
  }

  if (user?.role !== 'vendor') return <Outlet />;

  // No vendor row yet — they never submitted the setup form. Allow
  // /vendor/profile so they can submit it; everything else shows the
  // setup-required screen.
  if (!vendor) {
    if (location.pathname === PROFILE_PATH) return <Outlet />;
    return <PendingApproval status="setup" />;
  }

  const status = vendor.approvalStatus || vendor.approval_status || 'pending';

  if (status === 'approved') return <Outlet />;

  if (location.pathname === PROFILE_PATH) return <Outlet />;

  return <PendingApproval status={status} vendor={vendor} />;
};

export default VendorApprovalGate;
